/**
 * Klaviyo welcome offer: eligible PDP row + one-shot PDP popup opening.
 * -----------------------------------------------------------------------------------------------
 * Klaviyo remains the sole source of truth for eligibility. This script only treats the exact
 * market form's rendered teaser as an "eligible" signal; it never reconstructs audience rules
 * from cookies, localStorage, URL rules, previous purchases or subscription state.
 *
 * The first opening is remembered in localStorage across tabs/visits; session markers migrate.
 * Submission/eligibility state stays in sessionStorage. An exact market teaser may
 * also cache its own form ID for the rest of the current tab session, so a shopper who was already
 * approved does not wait for Klaviyo to repeat the same decision on every PDP. No URL, timer,
 * generic cookie or reconstructed audience rule can create that cache entry. On PDPs only, the
 * 15 seconds after the cookie choice or 25% genuine user scroll may request one permitted opening.
 * Gallery navigation is deliberately never an opening criterion. Manual row/teaser clicks remain
 * available afterwards.
 *
 * See docs/klaviyo-reopen-note.md.
 */
(() => {
  // A second asset evaluation must not install an independent timer/observer/controller.
  if (window.__lelafineWelcomeController) return;
  window.__lelafineWelcomeController = true;
  // Create Klaviyo's documented command queue before the consent-gated onsite library starts.
  // Some market builds consume and remove a queue that is created only after they are ready;
  // keeping this early reference lets later PDP clicks dispatch through Klaviyo on every market.
  const klaviyoOnsite = window._klOnsite = window._klOnsite || [];

  const FORM_IDS = Object.freeze({
    'lelafine.dk': 'Yj3Vt4',
    'lelafine.nl': 'YrGt3q',
    'lelafine.de': 'TSv7Mm',
    'lelafine.no': 'Srvbjm',
    'lelafine.be': 'S9wVut',
    'lelafine.se': 'YiLWGf',
  });

  const SESSION_LEGACY_RETIRED_KEY = 'lelafine:welcome-offer:retired';
  const SESSION_SUBMITTED_KEY = 'lelafine:welcome-offer:submitted';
  // Keep the historic storage key for continuity; it now means that any permitted welcome form
  // opening has been seen, not that the theme may auto-open a form.
  const SESSION_OPENED_KEY = 'lelafine:welcome-offer:auto-opened';
  const PERSISTENT_OPENED_KEY = 'lelafine:welcome-offer:opened';
  const SESSION_WISH_TRAFFIC_KEY = 'lelafine:welcome-offer:wish-traffic';
  const SESSION_ELIGIBLE_FORM_KEY = 'lelafine:welcome-offer:eligible-form';
  const SESSION_CONSENT_ANSWERED_AT_KEY = 'lelafine:welcome-offer:consent-answered-at';
  const SESSION_CONSENT_CONFIRMED_KEY = 'lelafine:welcome-offer:consent-confirmed-v2';
  const PERSISTENT_CLAIMED_KEY = 'lf_welcome_claimed';
  // Klaviyo's "show teaser before form" signal is intentionally rendered after two seconds.
  // Leave a little network/render margin before treating the page as ineligible.
  const ELIGIBILITY_DEADLINE_MS = 4_000;
  const FORM_OPEN_PERMIT_MS = 5_000;
  // After an internal Shopify navigation Klaviyo can retain the approved market eligibility in
  // this tab while its native teaser is no longer present on the new PDP. In that state the first
  // documented openForm command only wakes some market builds; a second user click used to be
  // required. Replay the command once, inside the same still-valid user permit, only when no open
  // event has arrived. A successful first command consumes the permit and cancels this fallback.
  const MANUAL_OPEN_RETRY_MS = 900;
  const PERMITTED_OPEN_CLOSE_GRACE_MS = 750;
  // Klaviyo swaps the opt-in view for the Success view after `submit`. The swap can briefly leave
  // no visible form in the DOM, so the one-shot guard must keep the already-permitted dialog alive
  // long enough for the dynamic coupon view to mount. Coupon activation runs independently in
  // welcome-discount-price.js and is never awaited here.
  const SUCCESS_TRANSITION_GRACE_MS = 5_000;
  const AUTO_OPEN_AFTER_CONSENT_MS = 15_000;
  const AUTO_OPEN_SCROLL_PROGRESS = 0.25;
  const AUTO_OPEN_IDLE_MS = 300;
  const USER_INPUT_WINDOW_MS = 1_800;

  const host = window.location.hostname.replace(/^www\./, '').toLowerCase();
  const marketFormId = FORM_IDS[host] || '';
  // All six live markets use the same one-shot opening state machine. The exact form remains
  // market-specific and is still the only authority that can grant eligibility.
  const usesOneShotFlow = Boolean(marketFormId);

  /** sessionStorage can throw in private mode. Automatic opening must then fail closed. */
  const session = {
    get(key) {
      try {
        return window.sessionStorage.getItem(key);
      } catch {
        return null;
      }
    },
    set(key, value) {
      try {
        window.sessionStorage.setItem(key, String(value));
        return true;
      } catch {
        return false;
      }
    },
    remove(key) {
      try {
        window.sessionStorage.removeItem(key);
        return true;
      } catch {
        return false;
      }
    },
  };

  const SESSION_STORAGE_PROBE_KEY = 'lelafine:welcome-offer:storage-probe';
  const sessionStorageUsable = (() => {
    const written = session.set(SESSION_STORAGE_PROBE_KEY, '1');
    const readable = written && session.get(SESSION_STORAGE_PROBE_KEY) === '1';
    session.remove(SESSION_STORAGE_PROBE_KEY);
    return readable;
  })();

  const now = Date.now();

  // Suppression only, never an audience grant. Keep it across tabs and future visits; if the
  // browser cannot retain the decision, fail closed for automatic openings and keep clicks usable.
  function persistentOpenSeen() {
    try {
      return window.localStorage.getItem(PERSISTENT_OPENED_KEY) === '1';
    } catch {
      return true;
    }
  }

  function persistOpenSeen() {
    try {
      window.localStorage.setItem(PERSISTENT_OPENED_KEY, '1');
      return window.localStorage.getItem(PERSISTENT_OPENED_KEY) === '1';
    } catch {
      return false;
    }
  }

  let eligible = false;
  let eligibilityClosed = false;
  const persistentWelcomeClaimed = (() => {
    try {
      return window.localStorage.getItem(PERSISTENT_CLAIMED_KEY) === '1';
    } catch {
      return document.documentElement.classList.contains('lelafine-welcome-claimed');
    }
  })();
  let retired = session.get(SESSION_SUBMITTED_KEY) === '1' || persistentWelcomeClaimed;
  const sessionEligibleForMarket = session.get(SESSION_ELIGIBLE_FORM_KEY) === marketFormId;
  // Before the all-market rollout, the five non-DK markets wrote `retired` after their first open.
  // Treat that legacy flag as a previously seen opening so an in-flight shopper keeps the same
  // closed/open state when the new script arrives during the session.
  let welcomeOpenSeen = usesOneShotFlow && (
    session.get(SESSION_OPENED_KEY) === '1'
    || session.get(SESSION_LEGACY_RETIRED_KEY) === '1'
    || persistentOpenSeen()
  );
  if (welcomeOpenSeen) {
    session.set(SESSION_OPENED_KEY, '1');
    persistOpenSeen();
  }
  let teaserObserver = null;
  let teaserOverlayObserver = null;
  let teaserOverlayFrame = 0;
  let nativeOfferRowReady = false;
  let eligibilityTimer = null;
  let openPermitKind = '';
  let openPermitUntil = 0;
  let openPermitScrollY = 0;
  let openPermitTimer = null;
  let manualOpenRetryTimer = null;
  let permittedPopupActive = false;
  let permittedPopupWasVisible = false;
  let permittedPopupOpenedAt = 0;
  let permittedPopupScrollY = null;
  let permittedPopupVisibilityTimer = null;
  let successTransitionUntil = 0;
  let successTransitionTimer = null;
  let formGuardObserver = null;
  let formGuardFrame = 0;
  let qaAutomaticReopenAttempted = false;
  let autoOpenRequested = false;
  let autoQualifiedReason = '';
  let autoOpenIdleTimer = null;
  let consentDelayTimer = null;
  let consentConfirmationTimer = null;
  let consentDelayQualified = false;
  let scrollQualified = false;
  let lastScrollInputAt = 0;
  let pointerStart = null;
  let touchStart = null;
  let autoObserversBound = false;
  let popupCloseScrollRestore = null;

  const isHomePage = () => window.location.pathname === '/';
  const isProductPage = () => window.location.pathname.includes('/products/');

  function cookieBannerVisible() {
    if (document.documentElement.classList.contains('lelafine-consent-pending')) return true;
    const cmp = document.querySelector('pandectes-cmp');
    const root = cmp?.shadowRoot;
    const dialog = root?.querySelector('dialog[open], .dialog.notice[open], .dialog.notice:not([hidden])');
    if (!dialog) return false;
    const style = getComputedStyle(dialog);
    const rect = dialog.getBoundingClientRect();
    return style.display !== 'none'
      && style.visibility !== 'hidden'
      && parseFloat(style.opacity || '1') > 0.05
      && rect.width > 1
      && rect.height > 1
      && rect.bottom > 0
      && rect.right > 0
      && rect.top < window.innerHeight
      && rect.left < window.innerWidth;
  }

  /**
   * Defensive veto only: Klaviyo still grants eligibility, but neither the PDP row nor the
   * theme-triggered popup may ever override Peter's Ønskeskyen/GoWish exclusion. Keep the veto
   * for the whole browser session because the acquisition parameters disappear on page two.
   */
  function isWishTraffic() {
    const source = new URLSearchParams(window.location.search).get('utm_source')?.trim().toLowerCase() || '';
    let referrerHost = '';

    try {
      referrerHost = new URL(document.referrer).hostname.replace(/^www\./, '').toLowerCase();
    } catch {
      /* Empty or non-URL referrer. The UTM remains the dependable signal. */
    }

    const sourceMatches = ['onskeskyen', 'ønskeskyen', 'gowish'].includes(source);
    const referrerMatches = referrerHost === 'onskeskyen.dk'
      || referrerHost.endsWith('.onskeskyen.dk')
      || referrerHost === 'gowish.com'
      || referrerHost.endsWith('.gowish.com');

    if (sourceMatches || referrerMatches) session.set(SESSION_WISH_TRAFFIC_KEY, '1');

    const blocked = session.get(SESSION_WISH_TRAFFIC_KEY) === '1';
    if (blocked) document.documentElement.classList.add('pkm-wish-traffic');
    return blocked;
  }

  // Resolve the sticky acquisition veto before the custom element can connect. The early async
  // loader may run before Shopify has parsed the PDP row, so the element must be able to inherit
  // the veto when it appears a few milliseconds later.
  const wishTrafficVetoed = isWishTraffic();

  function track(name, detail = {}) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: name,
      klaviyo_form_id: marketFormId,
      ...detail,
    });
  }

  function automaticOpenGateIsOpen() {
    return usesOneShotFlow
      && isProductPage()
      && eligible
      && !retired
      && !welcomeOpenSeen
      && !persistentOpenSeen()
      && !wishTrafficVetoed
      && sessionStorageUsable
      && !cookieBannerVisible()
      && !['manual-only', 'storage-fail-dialog'].includes(unpublishedThemeQaMode());
  }

  function stopConsentDelayClock() {
    window.clearTimeout(consentDelayTimer);
    consentDelayTimer = null;
    window.clearTimeout(consentConfirmationTimer);
    consentConfirmationTimer = null;
  }

  function stopAutomaticTriggers() {
    stopConsentDelayClock();
    window.clearTimeout(autoOpenIdleTimer);
    autoOpenIdleTimer = null;
  }

  async function requestAutomaticOpen(reason) {
    if (autoOpenRequested || !autoQualifiedReason || !automaticOpenGateIsOpen()) return false;
    // Serialize the claim across same-origin tabs. Without a reliable claim, manual opening is
    // safer than showing the same interruption twice. No timer or page navigation clears it.
    if (!navigator.locks?.request) return false;
    try {
      return await navigator.locks.request(PERSISTENT_OPENED_KEY, () => {
        if (autoOpenRequested || !automaticOpenGateIsOpen()) return false;
        return claimAutomaticOpen(reason);
      });
    } catch {
      return false;
    }
  }

  function claimAutomaticOpen(reason) {
    // No opening marker is written, so a later visit without BF access still gets the normal offer.
    if (window.bfInsider?.isActive?.()) return false;
    autoOpenRequested = true;

    // openForm bypasses Klaviyo targeting. The exact teaser eligibility gate above is mandatory,
    // and automatic opening is disabled unless both opening markers are durably writable.
    const sessionMarked = session.set(SESSION_OPENED_KEY, '1')
      && session.get(SESSION_OPENED_KEY) === '1';
    if (!sessionMarked || !persistOpenSeen()) {
      stopAutomaticTriggers();
      track('klaviyo_welcome_offer_auto_open_blocked', {
        reason: 'opening-storage-unavailable',
        criterion: reason,
      });
      return false;
    }

    armNextFormOpen(`automatic-${reason}`);
    rememberOpenWithoutHidingRow(`automatic-${reason}`);
    const opened = openForm(marketFormId);
    track('klaviyo_welcome_offer_open_requested', {
      reason: `automatic-${reason}`,
      opened,
    });
    return opened;
  }

  function scheduleQualifiedAutomaticOpen() {
    if (!autoQualifiedReason || autoOpenRequested) return;
    window.clearTimeout(autoOpenIdleTimer);
    if (cookieBannerVisible()) {
      autoOpenIdleTimer = window.setTimeout(scheduleQualifiedAutomaticOpen, 250);
      return;
    }
    if (!automaticOpenGateIsOpen()) return;
    autoOpenIdleTimer = window.setTimeout(() => {
      autoOpenIdleTimer = null;
      requestAutomaticOpen(autoQualifiedReason);
    }, AUTO_OPEN_IDLE_MS);
  }

  function qualifyAutomaticOpen(reason) {
    if (!autoQualifiedReason) {
      autoQualifiedReason = reason;
      if (unpublishedThemeQaMode()) {
        document.documentElement.dataset.pkmWelcomeQaCriterion = reason;
        document.documentElement.dataset.pkmWelcomeQaQualifiedAt = String(Date.now());
      }
      track('klaviyo_welcome_offer_auto_qualified', { criterion: reason });
    }
    scheduleQualifiedAutomaticOpen();
  }

  function scheduleConsentDelayCriterion() {
    if (consentDelayQualified || consentDelayTimer || !isProductPage()) return;
    const answeredAt = Number(session.get(SESSION_CONSENT_ANSWERED_AT_KEY));
    if (!Number.isFinite(answeredAt) || answeredAt <= 0) return;
    const remaining = Math.max(0, AUTO_OPEN_AFTER_CONSENT_MS - (Date.now() - answeredAt));
    consentDelayTimer = window.setTimeout(() => {
      consentDelayTimer = null;
      consentDelayQualified = true;
      qualifyAutomaticOpen('consent-answered-15s');
    }, remaining);
  }

  function noteConsentAnswered(source) {
    let answeredAt = Number(session.get(SESSION_CONSENT_ANSWERED_AT_KEY));
    const alreadyConfirmed = session.get(SESSION_CONSENT_CONFIRMED_KEY) === '1';
    if (!alreadyConfirmed || !Number.isFinite(answeredAt) || answeredAt <= 0) {
      answeredAt = Date.now();
      session.set(SESSION_CONSENT_ANSWERED_AT_KEY, answeredAt);
      session.set(SESSION_CONSENT_CONFIRMED_KEY, '1');
      track('klaviyo_welcome_offer_consent_answered', { source });
    }
    scheduleConsentDelayCriterion();
    [0, 350, 1_000].forEach((delay) => window.setTimeout(scheduleQualifiedAutomaticOpen, delay));
  }

  function hasConsentDecisionCookie() {
    return /(^|;\s*)_pandectes_gdpr=/.test(document.cookie);
  }

  function marketingConsentState() {
    const privacy = window.Shopify?.customerPrivacy;
    if (!privacy || typeof privacy.marketingAllowed !== 'function') return null;
    try {
      return privacy.marketingAllowed() === true;
    } catch {
      return null;
    }
  }

  /**
   * Pandectes may emit Shopify's visitorConsentCollected event while its initial undecided state
   * is mounting. Starting the clock from that event made a physical iPhone recording open about
   * 12 seconds after the actual Accept tap. Conversely, Safari private browsing may not expose
   * Pandectes' decision cookie to this first-party script even though Shopify has already granted
   * marketing processing and Klaviyo has rendered the exact eligible teaser. The cookie is
   * therefore only a wake-up hint; Shopify Customer Privacy plus a closed dialog is the authority.
   * Keep the low-frequency monitor alive while the customer considers the banner. A fixed two-
   * second retry window silently lost the timer when the choice came later than that window.
   */
  function confirmConsentAnswered(source) {
    window.clearTimeout(consentConfirmationTimer);
    consentConfirmationTimer = null;
    let attempts = 0;

    const confirm = () => {
      const marketingState = marketingConsentState();
      if (marketingState === true && !cookieBannerVisible()) {
        noteConsentAnswered(source);
        return;
      }
      attempts += 1;
      // An explicit rejection with a durable Pandectes decision needs no further polling. A later
      // preferences change emits a fresh event and starts a new confirmation transaction.
      if (marketingState === false && hasConsentDecisionCookie() && !cookieBannerVisible()) return;
      consentConfirmationTimer = window.setTimeout(confirm, attempts < 40 ? 50 : 500);
    };

    confirm();
  }

  function evaluateScrollCriterion() {
    if (!isProductPage() || scrollQualified) return;
    if (unpublishedThemeQaMode()) {
      const qaScrollable = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      document.documentElement.dataset.pkmWelcomeQaScrollProgress = qaScrollable
        ? (Math.max(0, window.scrollY) / qaScrollable).toFixed(4)
        : '0';
      document.documentElement.dataset.pkmWelcomeQaScrollInputAge = String(
        Date.now() - lastScrollInputAt
      );
    }
    if (Date.now() - lastScrollInputAt > USER_INPUT_WINDOW_MS) return;
    const scrollable = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    if (!scrollable) return;
    const progress = Math.max(0, window.scrollY) / scrollable;
    if (progress < AUTO_OPEN_SCROLL_PROGRESS) return;
    scrollQualified = true;
    qualifyAutomaticOpen('user-scroll-25pct');
  }

  function noteAutomaticInputActivity() {
    if (autoQualifiedReason) scheduleQualifiedAutomaticOpen();
  }

  function resetPageAutomaticCriteria() {
    stopAutomaticTriggers();
    autoOpenRequested = false;
    autoQualifiedReason = '';
    scrollQualified = false;
    lastScrollInputAt = 0;
    pointerStart = null;
    touchStart = null;
    scheduleConsentDelayCriterion();
  }

  function initializeAutomaticCriterionObservers() {
    if (autoObserversBound || !isProductPage()) return;
    autoObserversBound = true;

    const criterionEventIsTrusted = (event) => Boolean(
      event?.isTrusted || unpublishedThemeQaMode()
    );
    const markPageScrollInput = (event) => {
      if (event && !criterionEventIsTrusted(event)) return;
      lastScrollInputAt = Date.now();
      if (unpublishedThemeQaMode()) {
        document.documentElement.dataset.pkmWelcomeQaScrollInput = event?.type || 'trusted';
        document.documentElement.dataset.pkmWelcomeQaScrollInputAt = String(lastScrollInputAt);
      }
      noteAutomaticInputActivity();
    };
    const relevantScrollKey = (event) => [
      'ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End', ' ',
    ].includes(event.key);

    window.addEventListener('wheel', markPageScrollInput, { capture: true, passive: true });
    window.addEventListener('scroll', evaluateScrollCriterion, { passive: true });
    document.addEventListener('keydown', (event) => {
      if (!criterionEventIsTrusted(event)) return;
      noteAutomaticInputActivity();
      const interactiveTarget = event.target instanceof Element
        && Boolean(event.target.closest('button, a, input, select, textarea, [contenteditable="true"]'));
      if (relevantScrollKey(event) && !(event.key === ' ' && interactiveTarget)) {
        markPageScrollInput(event);
      }
    }, true);
    document.addEventListener('pointerdown', (event) => {
      if (!criterionEventIsTrusted(event)) return;
      noteAutomaticInputActivity();
      pointerStart = {
        x: event.clientX,
        y: event.clientY,
        pointerId: event.pointerId,
      };
    }, { capture: true, passive: true });
    document.addEventListener('pointermove', (event) => {
      if (!criterionEventIsTrusted(event)) return;
      if (!pointerStart || pointerStart.pointerId !== event.pointerId) return;
      noteAutomaticInputActivity();
      const dx = event.clientX - pointerStart.x;
      const dy = event.clientY - pointerStart.y;
      if (Math.abs(dy) >= 4 && Math.abs(dy) >= Math.abs(dx)) markPageScrollInput(event);
    }, { capture: true, passive: true });
    document.addEventListener('pointerup', () => { pointerStart = null; }, true);
    document.addEventListener('pointercancel', () => { pointerStart = null; }, true);
    document.addEventListener('touchstart', (event) => {
      if (!criterionEventIsTrusted(event)) return;
      noteAutomaticInputActivity();
      const touch = event.touches?.[0];
      touchStart = touch
        ? { x: touch.clientX, y: touch.clientY }
        : null;
    }, { capture: true, passive: true });
    document.addEventListener('touchmove', (event) => {
      if (!criterionEventIsTrusted(event)) return;
      const touch = event.touches?.[0];
      if (!touch || !touchStart) return;
      noteAutomaticInputActivity();
      const dx = touch.clientX - touchStart.x;
      const dy = touch.clientY - touchStart.y;
      if (Math.abs(dy) >= 4 && Math.abs(dy) >= Math.abs(dx)) markPageScrollInput(event);
    }, { capture: true, passive: true });
    document.addEventListener('touchend', () => { touchStart = null; }, true);
    document.addEventListener('click', (event) => {
      if (criterionEventIsTrusted(event)) noteAutomaticInputActivity();
    }, true);
    window.addEventListener('pagehide', stopConsentDelayClock);
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) resetPageAutomaticCriteria();
    });
  }

  function syncNotes(state) {
    document.querySelectorAll('klaviyo-reopen-note').forEach((note) => {
      if (typeof note.setState === 'function') note.setState(state);
    });
  }

  function offerRowCanBePresented() {
    const qaMode = unpublishedThemeQaMode();
    const qaCanUseFallback = [
      'eligible',
      'dialog',
      'late-dialog',
      'storage-fail-dialog',
      'manual-only',
    ].includes(qaMode);
    return nativeOfferRowReady || qaCanUseFallback;
  }

  function presentOfferRowAfterNativeSurface() {
    if (nativeOfferRowReady) return;
    nativeOfferRowReady = true;
    // The unpublished pending fixture deliberately proves zero-height first paint even if the
    // real onsite script happens to load during the test.
    if (unpublishedThemeQaMode() === 'pending') return;
    // A late native render must not reverse an explicit state transition that happened while the
    // surface was mounting. A genuine late eligibility grant first moves the row back to pending
    // in resolveEligible(), so it remains eligible for this one-way presentation commit.
    const presentationWasRevoked = [...document.querySelectorAll('klaviyo-reopen-note')]
      .some((note) => note.dataset.state === 'ineligible' || note.dataset.state === 'retired');
    if (presentationWasRevoked) return;
    syncNotes('eligible');
  }

  function setReopenLock(locked) {
    if (!usesOneShotFlow) return;
    if (locked) document.documentElement.dataset.pkmKlaviyoReopenLocked = 'true';
    else delete document.documentElement.dataset.pkmKlaviyoReopenLocked;
  }

  function qaStamp(value) {
    if (!unpublishedThemeQaMode()) return;
    document.documentElement.dataset.pkmWelcomeQaTrace = value;
  }

  function clearBlockedPopupShells() {
    document.querySelectorAll('[data-pkm-klaviyo-welcome-blocked]').forEach((element) => {
      delete element.dataset.pkmKlaviyoWelcomeBlocked;
    });
  }

  function armNextFormOpen(kind) {
    if (!usesOneShotFlow) return;
    cancelPopupCloseScrollRestore('new-deliberate-open');
    openPermitKind = kind;
    openPermitUntil = Date.now() + FORM_OPEN_PERMIT_MS;
    openPermitScrollY = Math.max(0, window.scrollY);
    window.clearTimeout(openPermitTimer);
    clearBlockedPopupShells();
    setReopenLock(false);
    qaStamp(`permit-armed:${kind}`);

    openPermitTimer = window.setTimeout(() => {
      openPermitKind = '';
      openPermitUntil = 0;
      if (!permittedPopupActive) setReopenLock(true);
    }, FORM_OPEN_PERMIT_MS + 50);
  }

  function consumeFormOpenPermit() {
    if (!openPermitKind || Date.now() > openPermitUntil) {
      qaStamp('permit-missing');
      return '';
    }
    const kind = openPermitKind;
    permittedPopupScrollY = openPermitScrollY;
    if (unpublishedThemeQaMode()) {
      document.documentElement.dataset.pkmWelcomeQaPermittedScrollY = String(
        permittedPopupScrollY
      );
    }
    openPermitKind = '';
    openPermitUntil = 0;
    openPermitScrollY = 0;
    window.clearTimeout(openPermitTimer);
    qaStamp(`permit-consumed:${kind}`);
    return kind;
  }

  /**
   * Klaviyo can attempt its own reopen after a close. The root lock prevents that attempt from
   * painting; this closes its internal form state so a later real teaser/row click still works.
   */
  function closeUnpermittedFormOpen() {
    setReopenLock(true);

    let closeRequested = false;
    const requestClose = () => {
      if (closeRequested) return;
      const scope = marketPopupNodes()[0] || klaviyoDialogCandidates()[0];
      if (!scope) return;
      scope.dataset.pkmKlaviyoWelcomeDialog = marketFormId;
      scope.dataset.pkmKlaviyoWelcomeBlocked = 'true';
      const closeButton = scope.querySelector([
        'button[aria-label*="close" i]',
        'button[aria-label*="luk" i]',
        'button[title*="close" i]',
        'button[title*="luk" i]',
      ].join(','));

      if (closeButton instanceof HTMLElement) {
        closeRequested = true;
        closeButton.click();
        return;
      }

      // Klaviyo currently supplies an accessible close button. Escape is a safe fallback if its
      // private markup changes before the button exists in the first render pass.
      const escape = new KeyboardEvent('keydown', {
        key: 'Escape',
        code: 'Escape',
        bubbles: true,
        cancelable: true,
      });
      document.dispatchEvent(escape);
      window.dispatchEvent(new KeyboardEvent('keydown', {
        key: 'Escape',
        code: 'Escape',
        bubbles: true,
        cancelable: true,
      }));
    };

    requestClose();
    window.requestAnimationFrame(requestClose);
    window.setTimeout(requestClose, 100);
    window.setTimeout(releaseStrandedScrollLock, 200);
  }

  function retire(reason) {
    if (retired) return;
    retired = true;
    stopAutomaticTriggers();
    session.set(SESSION_SUBMITTED_KEY, '1');
    session.remove(SESSION_ELIGIBLE_FORM_KEY);
    window.clearTimeout(permittedPopupVisibilityTimer);
    permittedPopupOpenedAt = 0;
    syncNotes('retired');
    stopMarketTeaserOverlayProbe();
    track('klaviyo_welcome_offer_retired', { reason });
  }

  window.addEventListener('welcome-discount:claimed', () => {
    document.documentElement.classList.add('lelafine-welcome-claimed');
    document.documentElement.classList.add('lelafine-welcome-prepaint-hidden');
    retire('dynamic-code-received');
  });
  window.addEventListener('storage', (event) => {
    if (event.key !== PERSISTENT_CLAIMED_KEY || event.newValue !== '1') return;
    document.documentElement.classList.add('lelafine-welcome-claimed');
    document.documentElement.classList.add('lelafine-welcome-prepaint-hidden');
    retire('dynamic-code-received-another-tab');
  });

  function preservePermittedPopupForSuccess() {
    if (!usesOneShotFlow || !permittedPopupActive) return false;
    successTransitionUntil = Date.now() + SUCCESS_TRANSITION_GRACE_MS;
    window.clearTimeout(successTransitionTimer);
    successTransitionTimer = window.setTimeout(() => {
      successTransitionTimer = null;
      successTransitionUntil = 0;
      scheduleFormGuard();
    }, SUCCESS_TRANSITION_GRACE_MS + 50);
    qaStamp('submit-success-transition');
    return true;
  }

  function rememberOpenWithoutHidingRow(reason) {
    const firstOpen = !welcomeOpenSeen;
    welcomeOpenSeen = true;
    session.set(SESSION_OPENED_KEY, '1');
    persistOpenSeen();
    stopAutomaticTriggers();
    startFormGuard();
    scheduleFormGuard();
    if (eligible) syncNotes('eligible');
    if (firstOpen) track('klaviyo_welcome_offer_opened_row_kept', { reason });
  }

  /**
   * Klaviyo's documented onsite queue. Eligibility is checked before this function is ever called.
   */
  function openQaDialog(formId) {
    const lockedScrollY = Math.max(0, window.scrollY);
    let dialog = document.querySelector('[data-pkm-welcome-qa-dialog]');
    let shouldApplyScrollLock = false;
    if (!dialog) {
      dialog = document.createElement('div');
      dialog.setAttribute('role', 'dialog');
      dialog.setAttribute('aria-modal', 'true');
      dialog.setAttribute('data-pkm-welcome-qa-dialog', 'true');
      dialog.setAttribute('data-pkm-welcome-qa-locked-scroll-y', String(lockedScrollY));
      dialog.className = 'needsclick kl-private-reset-css-pkm-qa';
      dialog.style.cssText = 'position:fixed;inset:0;z-index:2147483000;background:#f9f7f0;display:grid;place-items:center';
      dialog.innerHTML = [
        '<button type="button" aria-label="Close" data-pkm-welcome-qa-close style="position:absolute;right:16px;top:16px">×</button>',
        '<div style="display:grid;gap:16px;justify-items:center">',
        '<strong>PKM welcome popup QA</strong>',
        '<button type="button" data-pkm-welcome-qa-submit>QA submit</button>',
        '<button type="button" data-pkm-welcome-qa-success-close>QA success close</button>',
        '</div>',
      ].join('');
      const closeQaDialog = () => {
        const closeLockedScrollY = Number(dialog.dataset.pkmWelcomeQaLockedScrollY) || 0;
        // Real mobile Klaviyo keeps an aria-modal wrapper mounted and translates it off-screen.
        // Reproduce that teardown instead of removing the QA dialog, otherwise the watchdog test
        // misses the exact iPhone failure where the stale wrapper still looks "visible" by size.
        dialog.dataset.pkmWelcomeQaOffscreen = 'true';
        dialog.setAttribute('aria-hidden', 'true');
        dialog.style.setProperty('transform', 'translateX(-200vw)');
        dialog.style.setProperty('pointer-events', 'none');

        // Reproduce the second native iOS scroll mutation that can arrive after the first unlock.
        // This QA-only fault proves the close transaction protects the original viewport until the
        // Klaviyo wrapper has fully settled, instead of declaring success after its first frame.
        const lateNativeScrollY = Math.max(
          0,
          closeLockedScrollY - Math.min(420, Math.round(window.innerHeight / 2))
        );
        window.setTimeout(() => {
          if (!['dialog', 'late-dialog', 'storage-fail-dialog'].includes(unpublishedThemeQaMode())
            || !popupCloseScrollRestore
            || dialog.dataset.pkmWelcomeQaOffscreen !== 'true') return;
          window.scrollTo(window.scrollX, lateNativeScrollY);
          document.documentElement.dataset.pkmWelcomeQaLateScroll = 'true';
          qaStamp(`qa-late-native-scroll:${lateNativeScrollY}`);
        }, 650);

        if (qaAutomaticReopenAttempted) return;
        qaAutomaticReopenAttempted = true;
        window.setTimeout(() => openQaDialog(formId), 500);
      };
      dialog.querySelector('[data-pkm-welcome-qa-close]').addEventListener('click', closeQaDialog);
      dialog.querySelector('[data-pkm-welcome-qa-submit]').addEventListener('click', () => {
        window.dispatchEvent(new CustomEvent('klaviyoForms', {
          detail: { type: 'submit', formId },
        }));
      });
      dialog.querySelector('[data-pkm-welcome-qa-success-close]')
        .addEventListener('click', closeQaDialog);
      document.body.appendChild(dialog);
      shouldApplyScrollLock = true;
    } else if (dialog.dataset.pkmWelcomeQaOffscreen === 'true' && openPermitKind) {
      delete dialog.dataset.pkmWelcomeQaOffscreen;
      dialog.removeAttribute('aria-hidden');
      dialog.style.removeProperty('transform');
      dialog.style.setProperty('pointer-events', 'auto');
      dialog.dataset.pkmWelcomeQaLockedScrollY = String(lockedScrollY);
      shouldApplyScrollLock = true;
    }

    if (shouldApplyScrollLock) {
      dialog.setAttribute('data-pkm-welcome-qa-locked-scroll-y', String(lockedScrollY));
      // Reproduce Klaviyo's iOS lock: the viewport is stored as a negative fixed-body top while
      // window.scrollY itself resets to zero. Closing leaves that lock for the theme watchdog.
      document.body.style.setProperty('overflow', 'hidden', 'important');
      document.body.style.setProperty('position', 'fixed', 'important');
      document.body.style.setProperty('top', `-${lockedScrollY}px`, 'important');
      document.body.style.setProperty('width', '100%', 'important');
      window.scrollTo(window.scrollX, 0);
    }

    window.dispatchEvent(new CustomEvent('klaviyoForms', {
      detail: { type: 'open', formId },
    }));
    return true;
  }

  function openForm(formId) {
    if (!formId || window.bfInsider?.isActive?.()) return false;
    if (['dialog', 'late-dialog', 'storage-fail-dialog'].includes(unpublishedThemeQaMode())) {
      return openQaDialog(formId);
    }
    try {
      // Queue the documented custom trigger. Do not use
      // `window.klaviyo.openForm`: the legacy back-in-stock asset can expose a synthetic Proxy
      // before the real onsite library and make that method look callable without opening a form.
      // Keep using the original queue reference. Some Klaviyo market builds remove the public
      // `window._klOnsite` property after boot while retaining and patching the original array.
      klaviyoOnsite.push(['openForm', formId]);
      return true;
    } catch {
      return false;
    }
  }

  function scheduleManualOpenRetry(reason) {
    if (!usesOneShotFlow) return;
    window.clearTimeout(manualOpenRetryTimer);
    manualOpenRetryTimer = window.setTimeout(() => {
      manualOpenRetryTimer = null;
      const permitStillValid = Boolean(openPermitKind) && Date.now() <= openPermitUntil;
      if (permittedPopupActive || !permitStillValid || retired) return;

      const retried = openForm(marketFormId);
      track('klaviyo_welcome_offer_open_retried', { reason, opened: retried });
    }, MANUAL_OPEN_RETRY_MS);
  }

  function requestOpen(reason) {
    if (!eligible || retired || isHomePage()) return false;
    if (reason !== 'pdp-row-click') return false;

    if (usesOneShotFlow) {
      stopAutomaticTriggers();
      armNextFormOpen('manual-row-click');
      rememberOpenWithoutHidingRow(reason);
    }

    const opened = openForm(marketFormId);
    track('klaviyo_welcome_offer_open_requested', { reason, opened });

    scheduleManualOpenRetry('pdp-row-first-click-fallback');
    return opened;
  }

  function exactMarketTeaserExists() {
    if (!marketFormId) return false;
    const exactClass = `kl-teaser-${marketFormId}`;

    return [...document.querySelectorAll('[class*="kl-teaser-"]')].some((element) =>
      element.classList?.contains(exactClass)
    );
  }

  /**
   * Offer-row clicks must be real Klaviyo teaser clicks, not synthetic `openForm` commands.
   * Klaviyo accepts its own trusted teaser click consistently even when a market/browser build no
   * longer listens to the public command queue. Move that already target-approved teaser over the
   * visual PDP row as a transparent hit surface at every breakpoint, including iPhone Firefox.
   */
  function mountMarketTeaserOverlay() {
    if (!marketFormId || !isProductPage()) return false;

    const note = document.querySelector(
      `klaviyo-reopen-note[data-klaviyo-form-id="${marketFormId}"]`
    );
    const trigger = note?.querySelector('[data-klaviyo-reopen-trigger]');
    const teaser = [...document.querySelectorAll('[class*="kl-teaser-"]')].find((element) =>
      element.classList?.contains(`kl-teaser-${marketFormId}`)
    );
    const animated = teaser?.querySelector('[data-testid="animated-teaser"]');
    const clickSurface = teaser?.querySelector('[role="button"]');
    if (!(note instanceof HTMLElement)
      || !(trigger instanceof HTMLElement)
      || !(teaser instanceof HTMLElement)
      || !(animated instanceof HTMLElement)
      || !(clickSurface instanceof HTMLElement)) return false;
    if (teaser.dataset.pkmOfferRowOverlay === 'true') return true;

    note.style.position = 'relative';
    teaser.dataset.pkmOfferRowOverlay = 'true';
    teaser.setAttribute('aria-hidden', 'true');
    teaser.style.setProperty('position', 'absolute', 'important');
    teaser.style.setProperty('inset', '0', 'important');
    teaser.style.setProperty('width', '100%', 'important');
    teaser.style.setProperty('height', '100%', 'important');
    teaser.style.setProperty('margin', '0', 'important');
    teaser.style.setProperty('transform', 'none', 'important');
    teaser.style.setProperty('transform-origin', 'center', 'important');
    teaser.style.setProperty('opacity', '0', 'important');
    teaser.style.setProperty('visibility', 'visible', 'important');
    teaser.style.setProperty('pointer-events', 'auto', 'important');
    teaser.style.setProperty('z-index', '3', 'important');

    [animated, clickSurface].forEach((element) => {
      element.style.setProperty('position', 'absolute', 'important');
      element.style.setProperty('inset', '0', 'important');
      element.style.setProperty('width', '100%', 'important');
      element.style.setProperty('height', '100%', 'important');
      element.style.setProperty('min-height', '0', 'important');
      element.style.setProperty('padding', '0', 'important');
      element.style.setProperty('pointer-events', 'auto', 'important');
    });
    clickSurface.tabIndex = -1;

    note.appendChild(teaser);
    track('klaviyo_welcome_offer_native_teaser_overlay_mounted');
    return true;
  }

  function stopMarketTeaserOverlayProbe() {
    teaserOverlayObserver?.disconnect();
    teaserOverlayObserver = null;
    if (teaserOverlayFrame) window.cancelAnimationFrame(teaserOverlayFrame);
    teaserOverlayFrame = 0;
  }

  /**
   * Klaviyo stamps the market teaser class before its animated wrapper and role=button exist on
   * some mobile builds. The class is enough to grant eligibility, but not enough to mount the
   * trusted native hit surface. Keep a separate, idempotent observer alive until the complete
   * teaser has actually been moved over the visible offer row.
   */
  function scheduleMarketTeaserOverlay() {
    if (!eligible || retired || !marketFormId || !isProductPage()) return false;

    if (mountMarketTeaserOverlay()) {
      presentOfferRowAfterNativeSurface();
      stopMarketTeaserOverlayProbe();
      return true;
    }

    if (!teaserOverlayObserver) {
      teaserOverlayObserver = new MutationObserver(scheduleMarketTeaserOverlay);
      teaserOverlayObserver.observe(document.documentElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'role', 'data-testid'],
      });
    }

    if (!teaserOverlayFrame) {
      teaserOverlayFrame = window.requestAnimationFrame(() => {
        teaserOverlayFrame = 0;
        if (mountMarketTeaserOverlay()) {
          presentOfferRowAfterNativeSurface();
          stopMarketTeaserOverlayProbe();
        }
      });
    }

    return false;
  }

  /**
   * Office QA only. The switch is deliberately inert unless Shopify says this is not the MAIN
   * theme. It makes the unpublished duplicate testable without weakening live eligibility.
   */
  function unpublishedThemeQaMode() {
    const params = new URLSearchParams(window.location.search);
    const mode = params.get('pkm_welcome_qa') || '';
    if (![
      'eligible',
      'pending',
      'dialog',
      'late-dialog',
      'storage-fail-dialog',
      'teaser-race',
      'touch-teaser-race',
      'pre-pointer-teaser-race',
      'manual-only',
    ].includes(mode)) return '';
    const role = String(window.Shopify?.theme?.role || '').toLowerCase();
    return role && role !== 'main' ? mode : '';
  }

  function startQaTeaserRaceProbe() {
    const mount = () => {
      if (!document.body || document.querySelector('[data-pkm-welcome-qa-teaser-race]')) return;
      const teaser = document.createElement('div');
      teaser.className = `kl-teaser-${marketFormId}`;
      teaser.dataset.pkmWelcomeQaTeaserRace = 'true';
      document.body.appendChild(teaser);

      // Reproduce the live race: eligibility sees the exact class first; Klaviyo's nested trusted
      // click surface arrives in a later render pass.
      window.setTimeout(() => {
        const animated = document.createElement('div');
        animated.dataset.testid = 'animated-teaser';
        const clickSurface = document.createElement('button');
        clickSurface.type = 'button';
        clickSurface.setAttribute('role', 'button');
        clickSurface.addEventListener('click', () => openQaDialog(marketFormId));
        animated.appendChild(clickSurface);
        teaser.appendChild(animated);
      }, 120);
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', mount, { once: true });
    } else {
      mount();
    }
  }

  function startQaTouchTeaserRaceProbe() {
    const mount = () => {
      if (!document.body || document.querySelector('[data-pkm-welcome-qa-touch-teaser-race]')) return;
      const teaser = document.createElement('div');
      teaser.className = `kl-teaser-${marketFormId}`;
      teaser.dataset.pkmWelcomeQaTouchTeaserRace = 'true';
      teaser.style.cssText = 'position:fixed;top:120px;left:0;z-index:2147482000';

      const clickSurface = document.createElement('button');
      clickSurface.type = 'button';
      clickSurface.setAttribute('role', 'button');
      clickSurface.textContent = 'PKM touch teaser QA';
      clickSurface.style.cssText = 'min-width:120px;min-height:48px';
      // Reproduce the mobile Klaviyo ordering: its pointer handler can emit `open` before the
      // browser's later click event. The theme must have armed the manual permit in capture phase.
      clickSurface.addEventListener('pointerdown', () => openQaDialog(marketFormId));
      teaser.appendChild(clickSurface);
      document.body.appendChild(teaser);
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', mount, { once: true });
    } else {
      mount();
    }
  }

  function startQaPrePointerTeaserRaceProbe() {
    const mount = () => {
      if (!document.body || document.querySelector('[data-pkm-welcome-qa-pre-pointer-race]')) return;
      const teaser = document.createElement('div');
      teaser.className = `kl-teaser-${marketFormId}`;
      teaser.dataset.pkmWelcomeQaPrePointerRace = 'true';

      const clickSurface = document.createElement('button');
      clickSurface.type = 'button';
      clickSurface.setAttribute('role', 'button');
      clickSurface.addEventListener('touchstart', () => openQaDialog(marketFormId));
      teaser.appendChild(clickSurface);
      document.body.appendChild(teaser);

      // Model a browser/Klaviyo path where touchstart is the first activation signal and no
      // Pointer Event is available. On the broken runtime, `open` beats the click-only fallback.
      window.setTimeout(() => {
        clickSurface.dispatchEvent(new Event('touchstart', { bubbles: true, cancelable: true }));
      }, 120);
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', mount, { once: true });
    } else {
      mount();
    }
  }

  function startQaManualOnlyProbe() {
    const mount = () => {
      if (!document.querySelector('[data-pkm-welcome-qa-manual-only-trigger]')) {
        const teaser = document.createElement('div');
        teaser.className = `kl-teaser-${marketFormId}`;
        teaser.dataset.pkmWelcomeQaManualOnlyTrigger = 'true';
        teaser.style.cssText = 'position:fixed;top:120px;left:0;z-index:2147482000';

        const clickSurface = document.createElement('button');
        clickSurface.type = 'button';
        clickSurface.setAttribute('role', 'button');
        clickSurface.textContent = 'PKM manual-only teaser QA';
        clickSurface.style.cssText = 'min-width:120px;min-height:48px';
        clickSurface.addEventListener('click', () => openQaDialog(marketFormId));
        teaser.appendChild(clickSurface);
        document.body.appendChild(teaser);
      }

      window.setTimeout(() => openQaDialog(marketFormId), 250);
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', mount, { once: true });
    } else {
      mount();
    }
  }

  function startQaLayoutShiftProbe() {
    if (!('PerformanceObserver' in window)) return;
    const startedAt = window.performance.now();
    let total = 0;

    const publish = () => {
      document.querySelectorAll('klaviyo-reopen-note').forEach((note) => {
        note.setAttribute('data-qa-layout-shift', total.toFixed(6));
      });
    };

    try {
      const observer = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          if (!entry.hadRecentInput && entry.startTime >= startedAt) total += entry.value;
        });
        publish();
      });
      observer.observe({ type: 'layout-shift', buffered: false });
      publish();
      window.setTimeout(() => {
        publish();
        observer.disconnect();
      }, 800);
    } catch {
      /* LayoutShift is not available in every browser. Coordinate QA remains authoritative. */
    }
  }

  function resolveEligible(source) {
    if (eligible || eligibilityClosed || retired) return;
    eligible = true;
    eligibilityClosed = true;
    if (unpublishedThemeQaMode()) {
      document.documentElement.dataset.pkmWelcomeQaEligibleAt = String(Date.now());
    }
    session.set(SESSION_ELIGIBLE_FORM_KEY, marketFormId);
    teaserObserver?.disconnect();
    window.clearTimeout(eligibilityTimer);

    // On live PDPs, keep the custom row at zero height until Klaviyo's complete native click
    // surface is mounted over it. This prevents a session-cached eligibility grant from exposing
    // the unreliable openForm fallback for the first click after homepage → PDP navigation.
    syncNotes(offerRowCanBePresented() ? 'eligible' : 'pending');
    scheduleMarketTeaserOverlay();
    scheduleConsentDelayCriterion();
    scheduleQualifiedAutomaticOpen();
    track('klaviyo_welcome_offer_eligible', {
      source,
      eligibility_ms: Date.now() - now,
    });
  }

  function closeEligibilityWindow() {
    if (eligible || eligibilityClosed || retired) return;
    const consentAnsweredAt = Number(session.get(SESSION_CONSENT_ANSWERED_AT_KEY));
    if (!Number.isFinite(consentAnsweredAt) || consentAnsweredAt <= 0 || cookieBannerVisible()) {
      eligibilityTimer = window.setTimeout(closeEligibilityWindow, 250);
      return;
    }
    const postConsentRemaining = ELIGIBILITY_DEADLINE_MS - (Date.now() - consentAnsweredAt);
    if (postConsentRemaining > 0) {
      eligibilityTimer = window.setTimeout(closeEligibilityWindow, postConsentRemaining);
      return;
    }
    // Collapse the pending row after the short first-render window, but keep the exact-form
    // observer alive. Consent managers can load Klaviyo much later; a late, exact market teaser
    // is still authoritative eligibility and must be allowed to expand the row safely.
    syncNotes(retired ? 'retired' : 'ineligible');
    track('klaviyo_welcome_offer_ineligible_timeout', {
      eligibility_ms: Date.now() - now,
    });
  }

  function startEligibilityProbe(source = 'klaviyo-teaser') {
    teaserObserver?.disconnect();
    window.clearTimeout(eligibilityTimer);
    eligibilityClosed = false;

    if (exactMarketTeaserExists()) {
      resolveEligible(source);
      return;
    }

    teaserObserver = new MutationObserver(() => {
      if (exactMarketTeaserExists()) resolveEligible(source);
    });
    // Klaviyo can append an empty private wrapper first and stamp the market teaser class in a
    // later render pass. Watch that class change as well as inserted nodes on every market so
    // the offer expands on the first authoritative eligibility signal instead of the next mutation.
    teaserObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });
    eligibilityTimer = window.setTimeout(closeEligibilityWindow, ELIGIBILITY_DEADLINE_MS);
  }

  /** Laid out but invisible still counts as gone — that is how the teaser is hidden. */
  function synlig(element) {
    if (!element || !element.isConnected) return false;
    const style = getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden' || parseFloat(style.opacity) < 0.05) return false;
    const rect = element.getBoundingClientRect();
    return rect.width > 1 && rect.height > 1;
  }

  function popupSurfaceFor(form) {
    return form.closest('[role="dialog"], [aria-modal="true"]') || form;
  }

  function popupShellFor(form) {
    const surface = popupSurfaceFor(form);
    let shell = surface;
    let parent = surface.parentElement;

    // Klaviyo's click-blocking backdrop is outside role=dialog. Walk only through full-viewport,
    // fixed/absolute private wrappers and tag the outermost one; never tag body or a shop wrapper.
    while (parent && parent !== document.body && parent !== document.documentElement) {
      const style = getComputedStyle(parent);
      const rect = parent.getBoundingClientRect();
      const coversViewport = rect.width >= window.innerWidth * 0.8
        && rect.height >= window.innerHeight * 0.8;
      if (coversViewport && (style.position === 'fixed' || style.position === 'absolute')) shell = parent;
      parent = parent.parentElement;
    }

    return shell;
  }

  function klaviyoDialogCandidates() {
    return [...document.querySelectorAll('[role="dialog"], [aria-modal="true"]')].filter((element) => {
      const className = String(element.className || '');
      return element.classList.contains('needsclick')
        || className.includes('kl-private-reset')
        || Boolean(element.querySelector('[class*="kl-private-reset"], .needsclick'));
    });
  }

  function stampPermittedPopupDialog() {
    if (!permittedPopupActive || !marketFormId) return;
    klaviyoDialogCandidates().filter(synlig).forEach((dialog) => {
      dialog.dataset.pkmKlaviyoWelcomeDialog = marketFormId;
      delete dialog.dataset.pkmKlaviyoWelcomeBlocked;
    });
  }

  function marketPopupNodes() {
    if (!marketFormId) return [];
    const nodes = new Set(
      document.querySelectorAll(`[data-pkm-klaviyo-welcome-dialog="${marketFormId}"]`)
    );
    document.querySelectorAll(`.klaviyo-form-${marketFormId}`).forEach((form) => {
      nodes.add(popupShellFor(form));
    });
    return [...nodes];
  }

  function popupVisibleOnScreen(form) {
    const surface = popupSurfaceFor(form);
    if (!synlig(surface) && !synlig(form)) return false;
    const rect = surface.getBoundingClientRect();
    return rect.right > 0
      && rect.left < window.innerWidth
      && rect.bottom > 0
      && rect.top < window.innerHeight;
  }

  /**
   * Klaviyo does not consistently emit a close event on iOS. The DOM is therefore the final
   * authority for the lifetime of an already-permitted popup. Once that popup has been visible and
   * then disappears, the reopen lock is armed before the next paint. A later Klaviyo reinsert is
   * tagged in the same mutation microtask and cannot flash. A real row/teaser click clears the tag.
   */
  function enforceOneShotPopupState() {
    if (!usesOneShotFlow) return;

    stampPermittedPopupDialog();
    const forms = marketPopupNodes();
    // Klaviyo keeps a full-size form mounted after close and translates it towards the edge teaser.
    // Width/height alone therefore lies: an off-screen form is closed and must arm the lock.
    const visiblePopupExists = forms.some(popupVisibleOnScreen);

    if (permittedPopupActive) {
      if (visiblePopupExists) {
        permittedPopupWasVisible = true;
        window.clearTimeout(permittedPopupVisibilityTimer);
        return;
      }

      // A submit is not a close. Klaviyo briefly unmounts the opt-in view before mounting the
      // Success view with the unique coupon. Keep the deliberate dialog permitted during that
      // bounded handoff; an actual close event (or disappearance after the grace window) still
      // locks the form and restores the PDP scroll position as before.
      if (Date.now() < successTransitionUntil) return;

      if (permittedPopupWasVisible) {
        const restoreScrollY = permittedPopupScrollY;
        permittedPopupActive = false;
        permittedPopupWasVisible = false;
        permittedPopupOpenedAt = 0;
        permittedPopupScrollY = null;
        window.clearTimeout(permittedPopupVisibilityTimer);
        setReopenLock(true);
        schedulePopupCloseScrollRestore(restoreScrollY);
      }
      return;
    }

    // A deliberate manual open is allowed to build its DOM until its matching `open` event
    // consumes the permit. Everything else is a Klaviyo-owned automatic open and remains hidden.
    if (openPermitKind && Date.now() <= openPermitUntil) return;

    setReopenLock(true);
    forms.forEach((form) => {
      popupShellFor(form).dataset.pkmKlaviyoWelcomeBlocked = 'true';
    });

    // Current Klaviyo builds omit `.klaviyo-form-<ID>` from the full-screen dialog. Once the
    // welcome opening has been spent, a newly inserted Klaviyo dialog is therefore blocked by its
    // real private wrapper as well. Back-in-stock owns a separate modal and must stay available.
    let newlyBlockedDialog = false;
    klaviyoDialogCandidates().forEach((dialog) => {
      if (dialog.closest('#klaviyo-bis-modal') || dialog.querySelector('#klaviyo-bis-modal')) return;
      if (dialog.dataset.pkmKlaviyoWelcomeDialog !== marketFormId) newlyBlockedDialog = true;
      dialog.dataset.pkmKlaviyoWelcomeDialog = marketFormId;
      dialog.dataset.pkmKlaviyoWelcomeBlocked = 'true';
    });
    if (newlyBlockedDialog) {
      qaStamp('dom-guard-blocked-dialog');
      closeUnpermittedFormOpen();
    }
  }

  function scheduleFormGuard() {
    if (formGuardFrame) return;
    formGuardFrame = window.requestAnimationFrame(() => {
      formGuardFrame = 0;
      enforceOneShotPopupState();
    });
  }

  function startFormGuard() {
    if (!usesOneShotFlow || formGuardObserver) return;
    formGuardObserver = new MutationObserver(scheduleFormGuard);
    formGuardObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'aria-hidden', 'aria-modal', 'role'],
    });
    scheduleFormGuard();
  }

  /** A visible Klaviyo popup, excluding a teaser in both directions. */
  function klaviyoPopupOppe() {
    return [...document.querySelectorAll('.klaviyo-form, [class*="kl-private-reset"]')].some((element) => {
      if (element.closest('[class*="kl-teaser-"]') || element.querySelector('[class*="kl-teaser-"]')) return false;
      return popupVisibleOnScreen(element);
    });
  }

  /** A visible shop-owned modal/drawer that legitimately owns the scroll lock. */
  function andenEjerAfLaasen() {
    return [...document.querySelectorAll(
      'dialog[open], [aria-modal="true"], x-drawer[open], cart-drawer[open], .drawer[open]'
    )].some((element) => {
      // Klaviyo's closed mobile wrapper can remain mounted with aria-modal="true" while translated
      // off-screen. It is handled above and must never masquerade as a separate shop-owned modal.
      const className = String(element.className || '');
      const isKlaviyoSurface = element.hasAttribute('data-pkm-klaviyo-welcome-dialog')
        || element.hasAttribute('data-pkm-welcome-qa-dialog')
        || element.classList.contains('needsclick')
        || className.includes('kl-private-reset')
        || Boolean(element.querySelector('[class*="kl-private-reset"], .needsclick'));
      return !isKlaviyoSurface && popupVisibleOnScreen(element);
    });
  }

  function releaseStrandedScrollLock(expectedScrollY = null) {
    if (klaviyoPopupOppe() || andenEjerAfLaasen()) return false;

    // Klaviyo locks a scrolled page by fixing the document and storing the real viewport position
    // as a negative `top`. Removing that fixed state without restoring the offset sends the shopper
    // to the top of the PDP. Capture the lock offset before touching any styles, then put the
    // viewport back exactly where it was after the stranded lock has been released.
    const hasExpectedScrollY = Number.isFinite(expectedScrollY) && expectedScrollY >= 0;
    let preservedScrollY = hasExpectedScrollY
      ? expectedScrollY
      : Math.max(0, window.scrollY);
    let removedFixedScrollLock = false;

    [document.body, document.documentElement].forEach((element) => {
      const style = getComputedStyle(element);
      if (style.overflow === 'hidden' || style.overflowY === 'hidden') {
        element.style.setProperty('overflow', 'visible', 'important');
        if (style.position === 'fixed') {
          const fixedTop = Number.parseFloat(style.top);
          if (Number.isFinite(fixedTop) && fixedTop < 0) {
            preservedScrollY = Math.max(preservedScrollY, Math.round(-fixedTop));
          }
          removedFixedScrollLock = true;
          element.style.removeProperty('position');
          element.style.removeProperty('top');
          element.style.removeProperty('width');
        }
      }
    });

    if ((removedFixedScrollLock || hasExpectedScrollY)
      && Math.abs(window.scrollY - preservedScrollY) > 1) {
      window.scrollTo(window.scrollX, preservedScrollY);
    }

    return true;
  }

  function finishPopupCloseScrollRestore(transaction, reason) {
    if (!transaction || popupCloseScrollRestore !== transaction) return;
    window.cancelAnimationFrame(transaction.frame);
    window.clearTimeout(transaction.fallbackTimer);
    transaction.cancelEvents.forEach((eventName) => {
      window.removeEventListener(eventName, transaction.cancelOnUserInput, true);
    });
    popupCloseScrollRestore = null;

    if (['dialog', 'late-dialog', 'storage-fail-dialog'].includes(unpublishedThemeQaMode())) {
      const delta = Math.round(window.scrollY - transaction.expectedScrollY);
      document.documentElement.dataset.pkmWelcomeQaScrollDelta = String(delta);
      document.documentElement.dataset.pkmWelcomeQaScrollFinish = reason;
      qaStamp(`scroll-close-${reason}:${delta}`);
    }
  }

  function cancelPopupCloseScrollRestore(reason = 'cancelled') {
    if (!popupCloseScrollRestore) return;
    finishPopupCloseScrollRestore(popupCloseScrollRestore, reason);
  }

  function schedulePopupCloseScrollRestore(expectedScrollY) {
    const hasExpectedScrollY = Number.isFinite(expectedScrollY) && expectedScrollY >= 0;

    // The DOM guard and Klaviyo close event can observe the same close. Join the existing restore
    // transaction so a later signal cannot replace the authoritative pre-open position with null.
    if (popupCloseScrollRestore) {
      if (hasExpectedScrollY && !popupCloseScrollRestore.hasAuthoritativeExpected) {
        popupCloseScrollRestore.expectedScrollY = expectedScrollY;
        popupCloseScrollRestore.hasAuthoritativeExpected = true;
      }
      return;
    }

    const transaction = {
      expectedScrollY: hasExpectedScrollY ? expectedScrollY : Math.max(0, window.scrollY),
      hasAuthoritativeExpected: hasExpectedScrollY,
      // Klaviyo returns focus to its teaser about 1.9 seconds after an X close in Chromium. Keep
      // the original viewport authoritative through that delayed focus handoff as well as iOS'
      // native unlock. Trusted user input still cancels the transaction immediately.
      deadline: window.performance.now() + 2_800,
      frame: 0,
      fallbackTimer: 0,
      cancelEvents: ['touchstart', 'pointerdown', 'wheel', 'keydown'],
      cancelOnUserInput: null,
    };
    transaction.cancelOnUserInput = () => {
      finishPopupCloseScrollRestore(transaction, 'user-scroll');
    };
    transaction.cancelEvents.forEach((eventName) => {
      window.addEventListener(eventName, transaction.cancelOnUserInput, {
        capture: true,
        passive: true,
      });
    });
    popupCloseScrollRestore = transaction;

    // Klaviyo's off-screen handoff and native iOS unlock finish in separate frames. Preserve the
    // original viewport during the bounded settlement window, even after the first successful
    // unlock. Any new trusted scroll input cancels immediately, so this never fights the shopper.
    const settle = () => {
      if (popupCloseScrollRestore !== transaction) return;
      releaseStrandedScrollLock(transaction.expectedScrollY);
      if (window.performance.now() >= transaction.deadline) {
        finishPopupCloseScrollRestore(transaction, 'settled');
        return;
      }
      transaction.frame = window.requestAnimationFrame(settle);
    };
    transaction.frame = window.requestAnimationFrame(settle);
    transaction.fallbackTimer = window.setTimeout(() => {
      if (popupCloseScrollRestore !== transaction) return;
      releaseStrandedScrollLock(transaction.expectedScrollY);
      finishPopupCloseScrollRestore(transaction, 'fallback-settled');
    }, 3_000);
  }

  /**
   * Klaviyo's Success buttons use CLOSE_FORM, but that action can tear down the private wrapper
   * without first emitting the same close event as the X button. Arm the exact same restoration
   * transaction in capture phase, before Klaviyo's handler can unlock the body or move the
   * viewport. This is deliberately limited to the already-submitted Success view; the signup
   * submit button itself must never be treated as a close.
   */
  function capturePermittedPopupCloseIntent(event) {
    if (!usesOneShotFlow || !permittedPopupActive || !(event.target instanceof Element)) return;
    const dialog = event.target.closest(
      `[data-pkm-klaviyo-welcome-dialog="${marketFormId}"], [data-pkm-welcome-qa-dialog]`
    );
    if (!dialog || !popupVisibleOnScreen(dialog)) return;

    const control = event.target.closest('button, a, [role="button"]');
    if (!control) return;
    const accessibleName = [
      control.getAttribute('aria-label'),
      control.getAttribute('title'),
      control.textContent,
    ].filter(Boolean).join(' ').trim().toLowerCase();
    const explicitClose = /(^|\s)(close|luk|schlie(?:ß|ss)en|sluit|stäng|stänga|lukk)(\s|$)/i
      .test(accessibleName);
    const successClose = retired
      && !control.matches('button[type="submit"], input[type="submit"]');
    if (!explicitClose && !successClose) return;

    const expectedScrollY = permittedPopupScrollY;
    // A focused control inside Klaviyo's translated-offscreen wrapper can make Chromium scroll the
    // page again after the restore transaction has settled. Closing relinquishes that focus; the
    // form library remains responsible for returning focus to its opener.
    if (control instanceof HTMLElement) control.blur();
    if (unpublishedThemeQaMode()) {
      document.documentElement.dataset.pkmWelcomeQaCloseIntent = successClose
        ? 'success-control'
        : 'explicit-close';
      document.documentElement.dataset.pkmWelcomeQaCloseIntentScrollY = String(window.scrollY);
      document.documentElement.dataset.pkmWelcomeQaCloseExpectedScrollY = String(
        Number.isFinite(expectedScrollY) ? expectedScrollY : window.scrollY
      );
    }
    schedulePopupCloseScrollRestore(expectedScrollY);
  }

  function startVagt() {
    window.setInterval(() => {
      if (popupCloseScrollRestore) return;
      const laast = [document.body, document.documentElement].some((element) => {
        const style = getComputedStyle(element);
        return style.overflow === 'hidden' || style.overflowY === 'hidden';
      });
      if (laast) releaseStrandedScrollLock();
    }, 700);
  }

  if (!window.__klaviyoReopenBound) {
    window.__klaviyoReopenBound = true;
    startVagt();

    // Capture before Klaviyo's own CLOSE_FORM handler. `click` is intentional: on touch devices it
    // arrives after touchstart/pointerdown, so the same gesture cannot cancel the new transaction.
    document.addEventListener('click', capturePermittedPopupCloseIntent, true);

    // A touch/pointer press is also the start of a scroll. Let the browser decide whether the
    // gesture completes as a click. Stop Klaviyo's early handlers without preventDefault, so native
    // scrolling remains available. Only the completed trusted click may arm a permit or retry.
    const manualOfferTarget = (event) => {
      if (!(event.target instanceof Element)) return null;
      return event.target.closest(
        `klaviyo-reopen-note[data-klaviyo-form-id="${marketFormId}"] [data-klaviyo-reopen-trigger], .kl-teaser-${marketFormId}`
      );
    };
    const deferManualWelcomePress = (event) => {
      if (usesOneShotFlow && manualOfferTarget(event)) event.stopImmediatePropagation();
    };
    const armManualWelcomeOpen = (event) => {
      if (!event.isTrusted || event.button > 0) return;
      if (!usesOneShotFlow || !isProductPage() || !eligible || retired || wishTrafficVetoed) return;
      const clickedOfferRow = event.target.closest(
        `klaviyo-reopen-note[data-klaviyo-form-id="${marketFormId}"] [data-klaviyo-reopen-trigger]`
      );
      const clickedMarketTeaser = event.target.closest(`.kl-teaser-${marketFormId}`);
      if (!clickedOfferRow && !clickedMarketTeaser) return;
      if (permittedPopupActive) return;
      if (openPermitKind && Date.now() <= openPermitUntil) return;
      stopAutomaticTriggers();
      armNextFormOpen('manual-user-click');
      rememberOpenWithoutHidingRow('manual-user-click');
      if (clickedMarketTeaser) {
        scheduleManualOpenRetry('native-teaser-first-click-fallback');
      }
    };
    document.addEventListener('touchstart', deferManualWelcomePress, { capture: true, passive: true });
    document.addEventListener('pointerdown', deferManualWelcomePress, true);
    document.addEventListener('mousedown', deferManualWelcomePress, true);
    document.addEventListener('click', armManualWelcomeOpen, true);

    window.addEventListener('klaviyoForms', (event) => {
      const detail = event.detail || {};
      const { type, formId } = detail;

      // Back-in-stock, embedded signup and forms for another market must do absolutely nothing.
      if (!marketFormId || formId !== marketFormId) return;

      if (type === 'open') {
        if (usesOneShotFlow) {
          window.clearTimeout(manualOpenRetryTimer);
          manualOpenRetryTimer = null;
          const permit = consumeFormOpenPermit();
          if (!permit) {
            qaStamp('event-open-blocked');
            startFormGuard();
            scheduleFormGuard();
            closeUnpermittedFormOpen();
            track('klaviyo_welcome_offer_open_blocked', { reason: 'no-user-permit' });
            return;
          }
          clearBlockedPopupShells();
          qaStamp(`event-open-permitted:${permit}`);
          permittedPopupActive = true;
          permittedPopupWasVisible = false;
          permittedPopupOpenedAt = Date.now();
          window.clearTimeout(permittedPopupVisibilityTimer);
          permittedPopupVisibilityTimer = window.setTimeout(() => {
            if (!permittedPopupActive || permittedPopupWasVisible) return;
            permittedPopupActive = false;
            permittedPopupOpenedAt = 0;
            permittedPopupScrollY = null;
            setReopenLock(true);
            scheduleFormGuard();
          }, 3_000);
          rememberOpenWithoutHidingRow(`matching-form-opened:${permit}`);
          scheduleFormGuard();
        }
      }
      if (type === 'submit') {
        preservePermittedPopupForSuccess();
        retire('matching-form-submitted');
        scheduleFormGuard();
        return;
      }

      if (type === 'close') {
        const deliberateOpenStillPending = Boolean(openPermitKind)
          && Date.now() <= openPermitUntil;
        const permittedOpenStillPainting = permittedPopupActive
          && Date.now() - permittedPopupOpenedAt < PERMITTED_OPEN_CLOSE_GRACE_MS;
        const ignoreStaleClose = deliberateOpenStillPending || permittedOpenStillPainting;
        qaStamp(ignoreStaleClose
          ? (deliberateOpenStillPending ? 'event-close-ignored-pending' : 'event-close-ignored-grace')
          : 'event-close-locked');

        if (usesOneShotFlow && !ignoreStaleClose) {
          const restoreScrollY = permittedPopupScrollY;
          permittedPopupActive = false;
          permittedPopupWasVisible = false;
          permittedPopupOpenedAt = 0;
          permittedPopupScrollY = null;
          window.clearTimeout(permittedPopupVisibilityTimer);
          setReopenLock(true);
          scheduleFormGuard();
          schedulePopupCloseScrollRestore(restoreScrollY);
        }
        if (usesOneShotFlow && eligible && !retired) syncNotes('eligible');
        if (!usesOneShotFlow) {
          window.setTimeout(releaseStrandedScrollLock, 400);
          window.setTimeout(releaseStrandedScrollLock, 1_500);
        }
      }
    });

    // On a fresh private visit Klaviyo may load only after marketing consent. Re-open one short,
    // fail-closed probe then; the exact teaser must still prove account-side eligibility.
    document.addEventListener('consent:scripts-unblocked', () => {
      confirmConsentAnswered('consent:scripts-unblocked');
      if (!marketFormId || eligible || retired || wishTrafficVetoed) return;
      startEligibilityProbe('klaviyo-teaser-after-consent');
    });
    document.addEventListener('visitorConsentCollected', () => confirmConsentAnswered('visitorConsentCollected'));
    window.addEventListener('PandectesConsentUpdateLegacy', () => confirmConsentAnswered('PandectesConsentUpdateLegacy'));
  }

  if (!customElements.get('klaviyo-reopen-note')) {
    class KlaviyoReopenNote extends HTMLElement {
      connectedCallback() {
        this.trigger = this.querySelector('[data-klaviyo-reopen-trigger]');
        this._onClick = (event) => {
          event.preventDefault();
          if (!event.isTrusted) return;
          requestOpen('pdp-row-click');
        };
        this.trigger?.addEventListener('click', this._onClick);

        const idMatchesMarket = this.dataset.klaviyoFormId === marketFormId;
        this.setState(
          retired || !idMatchesMarket
            ? 'retired'
            : (wishTrafficVetoed
              ? 'ineligible'
              : (eligible && offerRowCanBePresented() ? 'eligible' : 'pending'))
        );
      }

      disconnectedCallback() {
        this.trigger?.removeEventListener('click', this._onClick);
      }

      setState(state) {
        if (this.dataset.serverHidden === 'true'
          || document.documentElement.classList.contains('lelafine-welcome-claimed')) {
          state = 'retired';
        }
        this.dataset.state = state;
        // Pending is deliberately zero-height: only the exact market teaser is allowed to make
        // the campaign row visible. Showing the server-rendered row before that proof creates a
        // first-paint flash and can promise a discount to an ineligible visitor.
        const presented = state === 'eligible';
        const interactive = state === 'eligible';
        this.hidden = state === 'pending' || state === 'retired';
        this.setAttribute('aria-hidden', presented ? 'false' : 'true');
        if (this.trigger) this.trigger.tabIndex = interactive ? 0 : -1;
        if (interactive) window.requestAnimationFrame(scheduleMarketTeaserOverlay);
      }
    }

    customElements.define('klaviyo-reopen-note', KlaviyoReopenNote);
  }

  if (!marketFormId || retired) {
    eligibilityClosed = true;
    syncNotes('retired');
    return;
  }

  if (usesOneShotFlow) {
    setReopenLock(true);
  }

  if (wishTrafficVetoed) {
    eligibilityClosed = true;
    syncNotes('ineligible');
    track('klaviyo_welcome_offer_ineligible', { reason: 'wish-traffic' });
    return;
  }

  initializeAutomaticCriterionObservers();

  if (hasConsentDecisionCookie() || marketingConsentState() === true) {
    confirmConsentAnswered('existing-pandectes-cookie');
  }

  const qaMode = unpublishedThemeQaMode();
  if (qaMode) startQaLayoutShiftProbe();
  if (qaMode === 'teaser-race') {
    startQaTeaserRaceProbe();
    resolveEligible('unpublished-theme-qa-teaser-race');
    return;
  }
  if (qaMode === 'touch-teaser-race') {
    startQaTouchTeaserRaceProbe();
    resolveEligible('unpublished-theme-qa-touch-teaser-race');
    return;
  }
  if (qaMode === 'pre-pointer-teaser-race') {
    startQaPrePointerTeaserRaceProbe();
    resolveEligible('unpublished-theme-qa-pre-pointer-teaser-race');
    return;
  }
  if (qaMode === 'manual-only') {
    resolveEligible('unpublished-theme-qa-manual-only');
    startQaManualOnlyProbe();
    return;
  }
  if (['eligible', 'dialog', 'storage-fail-dialog'].includes(qaMode)) {
    resolveEligible('unpublished-theme-qa');
    return;
  }
  if (qaMode === 'late-dialog') {
    window.setTimeout(() => resolveEligible('unpublished-theme-qa-late'), 12_000);
    return;
  }

  if (qaMode === 'pending') return;

  // A grant can only enter sessionStorage above after the exact form teaser existed on this host.
  // Reuse that same market-specific grant on later PDPs in the tab, until submit retires it. This
  // is deliberately PDP-only: the native teaser stays visually disabled on every non-PDP surface.
  if (sessionEligibleForMarket && isProductPage()) {
    resolveEligible('session-exact-market-teaser');
    return;
  }

  startEligibilityProbe();
})();
